"use client";

// Brand re-skin of components/marketing/GeoAuditTool.tsx for the
// redesigned /geo-audit page. Behavior is identical to the shared
// component: same request shape (POST /api/geo-audit with { url }),
// same response types, same result-rendering logic. Only the visual
// layer (classes, colors, copy casing) follows the new brand system.

import { FormEvent, useMemo, useState } from "react";
import Link from "next/link";

// ─── Types ────────────────────────────────────────────────

type AuditCheck = {
  key: string;
  label: string;
  passed: boolean;
  details: string;
  category?: string;
  weight?: number;
};

type CategoryBreakdown = {
  key: string;
  label: string;
  passed: number;
  total: number;
  score: number;
  weightedScore: number;
};

type AuditMetrics = {
  responseTimeMs: number;
  htmlBytes: number;
  wordCount: number;
  h1Count: number;
  h2h3Count: number;
  semanticLandmarksCount: number;
  crawlableInternalLinks: number;
  externalLinks: number;
  imageCount: number;
  imagesMissingAlt: number;
  imageAltCoverage: number;
  listCount: number;
  tableCount: number;
  internalLinkSampleChecked: number;
  internalLinkHealthy: number;
  internalLinkHealthRate: number;
  hasJsonLd: boolean;
  hasFaqSchema: boolean;
  hasArticleSchema: boolean;
  hasBreadcrumbSchema: boolean;
  hasCanonical: boolean;
  hasRobotsTxt: boolean;
  hasSitemap: boolean;
  hasLlmsTxt: boolean;
  hasAuthorAttribution: boolean;
  estimatedReadingMinutes: number;
};

type SampledPage = {
  url: string;
  status: number;
  responseTimeMs: number;
  titlePresent: boolean;
  metaDescriptionPresent: boolean;
  canonicalPresent: boolean;
  jsonLdPresent: boolean;
  wordCount: number;
};

type SitewideSample = {
  sampledPages: SampledPage[];
  sampleSize: number;
  avgResponseTimeMs: number;
  titleCoverage: number;
  metaDescriptionCoverage: number;
  canonicalCoverage: number;
  jsonLdCoverage: number;
};

type GeoSemanticCheck = {
  key: string;
  label: string;
  score: number;
  details: string;
};

type GeoSemanticReport = {
  overallScore: number;
  checks: GeoSemanticCheck[];
  priorities: string[];
};

type AuditResponse = {
  url: string;
  domain: string;
  score: number;
  weightedScore: number;
  summary: string;
  durationMs: number;
  checks: AuditCheck[];
  categoryBreakdown: CategoryBreakdown[];
  metrics: AuditMetrics;
  sitewideSample?: SitewideSample;
  geoSemantic?: GeoSemanticReport;
  recommendations: string[];
  aiInsights?: { model: string; summary: string; priorities: string[] };
};

// ─── Sub-components ───────────────────────────────────────

function ScoreGauge({ score, size = 120 }: { score: number; size?: number }) {
  const color = score >= 85 ? "#1F7A48" : score >= 60 ? "#D97706" : "#DC2626";
  return (
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: `conic-gradient(${color} ${score}%, #EAEAE7 ${score}% 100%)`,
      }}
    >
      <div
        className="absolute flex flex-col items-center justify-center rounded-full bg-white"
        style={{ inset: size * 0.12 }}
      >
        <p className="font-bold leading-none" style={{ fontSize: size * 0.22, color }}>{score}%</p>
        <p className="text-[10px] text-[#9CA1A8]">Score</p>
      </div>
    </div>
  );
}

function ScoreBar({ score, label, sub }: { score: number; label: string; sub?: string }) {
  const color = score >= 85 ? "#1F7A48" : score >= 60 ? "#D97706" : "#DC2626";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-[#1B1D21]">{label}</p>
          {sub && <p className="text-[11px] text-[#9CA1A8]">{sub}</p>}
        </div>
        <p className="text-xs font-bold" style={{ color }}>{score}%</p>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#EAEAE7]">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

function PassBadge({ passed }: { passed: boolean }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
      passed ? "bg-[#E8F5EE] text-[#1F7A48]" : "bg-[#FEE2E2] text-[#B91C1C]"
    }`}>
      {passed ? "Pass" : "Fail"}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────

type Tab = "overview" | "checks" | "geo-signals" | "action-plan";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "checks", label: "Checks" },
  { id: "geo-signals", label: "GEO signals" },
  { id: "action-plan", label: "Action plan" },
];

type AuditToolProps = { initialUrl?: string; inputId?: string };

export default function AuditTool({ initialUrl = "", inputId = "geoUrl" }: AuditToolProps = {}) {
  const [url, setUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditResponse | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const effectiveScore = result?.weightedScore ?? result?.score ?? 0;
  const scoreColor = effectiveScore >= 85 ? "#1F7A48" : effectiveScore >= 60 ? "#D97706" : "#DC2626";

  const passFail = useMemo(() => {
    if (!result) return { passed: 0, failed: 0 };
    const passed = result.checks.filter((c) => c.passed).length;
    return { passed, failed: result.checks.length - passed };
  }, [result]);

  const groupedChecks = useMemo(() => {
    if (!result) return [] as { category: string; checks: AuditCheck[] }[];
    const map = new Map<string, AuditCheck[]>();
    for (const c of result.checks) {
      const cat = c.category ?? "Other";
      const arr = map.get(cat) ?? [];
      arr.push(c);
      map.set(cat, arr);
    }
    return Array.from(map.entries()).map(([category, checks]) => ({ category, checks }));
  }, [result]);

  function toggleCategory(cat: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  }

  function exportJson() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `geo-audit-${result.domain}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(u);
  }

  function exportCsv() {
    if (!result) return;
    const rows = [["Check", "Category", "Passed", "Weight", "Details"]];
    for (const c of result.checks) {
      rows.push([c.label, c.category ?? "", c.passed ? "Yes" : "No", String(c.weight ?? ""), c.details]);
    }
    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `geo-audit-${result.domain}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(u);
  }

  async function runAudit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setActiveTab("overview");
    const trimmed = url.trim();
    if (!trimmed) { setError("Please enter a website URL."); return; }

    setLoading(true);
    try {
      const res = await fetch("/api/geo-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
      });
      const json = await res.json();
      if (!res.ok || json.error) { setError(json.error ?? "Could not run audit."); return; }
      setResult(json as AuditResponse);
    } catch {
      setError("Network error while running audit.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {/* ─── Form ─────────────────────────────────────────── */}
      <form
        onSubmit={runAudit}
        className="rounded-[12px] border border-[#EAEAE7] bg-white p-5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] sm:p-6"
      >
        <label htmlFor={inputId} className="mb-2 block text-[13px] font-medium text-[#1B1D21]">
          Website URL
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
          <input
            id={inputId}
            type="text"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="e.g., ranksector.com or https://ranksector.com"
            className="h-11 w-full min-w-0 rounded-[10px] border border-[#EAEAE7] bg-white px-3.5 text-[14px] leading-none text-[#1B1D21] outline-none transition-shadow placeholder:text-[#9CA1A8] focus:border-[#3D5BDB] focus:shadow-[0_0_0_3px_rgba(61,91,219,0.16)] sm:flex-1"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-11 w-full items-center justify-center whitespace-nowrap rounded-[10px] bg-[#3D5BDB] px-5 text-[14px] font-semibold leading-none text-white transition-colors hover:bg-[#3350C4] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
          >
            <span className="block">{loading ? "Analyzing…" : "Run GEO audit"}</span>
          </button>
        </div>
        {error && <p className="mt-3 text-[13px] text-[#DC2626]">{error}</p>}
      </form>

      {/* ─── Loading state ────────────────────────────────── */}
      {loading && (
        <div className="mt-4 flex flex-col items-center gap-3 rounded-[12px] border border-[#EAEAE7] bg-white p-6 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#EAEAE7] border-t-[#3D5BDB]" />
          <p className="text-sm font-medium text-[#1B1D21]">Running your GEO audit</p>
          <p className="text-xs text-[#6A6E75]">Fetching pages, checking signals, running semantic analysis. Takes about 15 to 30 seconds.</p>
        </div>
      )}

      {/* ─── Results ─────────────────────────────────────── */}
      {result && (
        <div className="mt-6 rounded-[12px] border border-[#EAEAE7] bg-white text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          {/* Header */}
          <div className="border-b border-[#EAEAE7] p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex items-center gap-4">
                <ScoreGauge score={effectiveScore} size={88} />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9CA1A8]">GEO audit · {result.domain}</p>
                  <p className="mt-0.5 text-xs text-[#9CA1A8] break-all">{result.url}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full border border-[#EAEAE7] bg-[#F9F9F7] px-2.5 py-1 text-[11px] font-medium text-[#1B1D21]">
                      {passFail.passed}/{result.checks.length} checks passed
                    </span>
                    <span className="rounded-full border border-[#EAEAE7] bg-[#F9F9F7] px-2.5 py-1 text-[11px] font-medium text-[#1B1D21]">
                      {result.durationMs}ms
                    </span>
                    <span
                      className="rounded-full px-2.5 py-1 text-[11px] font-medium"
                      style={{ background: `${scoreColor}18`, color: scoreColor }}
                    >
                      {effectiveScore >= 85 ? "Strong" : effectiveScore >= 60 ? "Medium" : "Low"} AI readiness
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#1B1D21]">{result.summary}</p>

            {result.recommendations && result.recommendations.length > 0 && (
              <div className="mt-4 rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB] p-4">
                <p className="mb-2 text-[13px] font-semibold text-[#92400E]">
                  Top priorities to fix first
                </p>
                <ol className="list-decimal space-y-1.5 pl-5 text-xs text-[#78350F] leading-[1.5]">
                  {result.recommendations.slice(0, 3).map((rec: string, i: number) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Tab navigation */}
          <div className="flex overflow-x-auto border-b border-[#EAEAE7]">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 border-b-2 px-4 py-3 text-[13px] font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-[#3D5BDB] text-[#3D5BDB]"
                    : "border-transparent text-[#6A6E75] hover:text-[#1B1D21]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* ── OVERVIEW TAB ─────────────────────────────── */}
            {activeTab === "overview" && (
              <div className="space-y-5">
                {/* Summary stats */}
                <div className="grid gap-3 md:grid-cols-4">
                  {[
                    { label: "Passed checks", value: String(passFail.passed), sub: `of ${result.checks.length}`, color: "#1F7A48" },
                    { label: "Failed checks", value: String(passFail.failed), sub: "priority fixes", color: "#DC2626" },
                    { label: "Reading time", value: result.metrics?.estimatedReadingMinutes ? `${result.metrics.estimatedReadingMinutes}min` : "n/a", sub: "estimated depth" },
                    { label: "Fetch speed", value: `${result.durationMs}ms`, sub: "initial response" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-[10px] border border-[#EAEAE7] bg-[#F9F9F7] px-3 py-3">
                      <p className="text-[11px] font-medium text-[#6A6E75]">{s.label}</p>
                      <p className="mt-1 text-2xl font-bold" style={{ color: s.color ?? "#1B1D21" }}>{s.value}</p>
                      <p className="text-xs text-[#9CA1A8]">{s.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Category breakdown + metrics grid */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[10px] border border-[#EAEAE7] bg-[#F9F9F7] p-4">
                    <p className="mb-3 text-[13px] font-semibold text-[#1B1D21]">Category breakdown</p>
                    <div className="space-y-3">
                      {result.categoryBreakdown.map((cat) => (
                        <ScoreBar
                          key={cat.key}
                          label={cat.label}
                          score={cat.weightedScore}
                          sub={`${cat.passed}/${cat.total} checks`}
                        />
                      ))}
                    </div>
                  </div>

                  {result.metrics && (
                    <div className="rounded-[10px] border border-[#EAEAE7] bg-[#F9F9F7] p-4">
                      <p className="mb-3 text-[13px] font-semibold text-[#1B1D21]">Page metrics</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: "HTML size", value: `${Math.round(result.metrics.htmlBytes / 1024)} KB` },
                          { label: "Word count", value: String(result.metrics.wordCount) },
                          { label: "H2/H3 headings", value: String(result.metrics.h2h3Count) },
                          { label: "Lists", value: String(result.metrics.listCount) },
                          { label: "Tables", value: String(result.metrics.tableCount) },
                          { label: "Images", value: String(result.metrics.imageCount) },
                          { label: "Image alt text", value: `${result.metrics.imageAltCoverage}%` },
                          { label: "Internal links", value: String(result.metrics.crawlableInternalLinks) },
                          { label: "External links", value: String(result.metrics.externalLinks) },
                          { label: "Link health", value: `${result.metrics.internalLinkHealthRate}%` },
                          { label: "Response", value: `${result.metrics.responseTimeMs}ms` },
                          { label: "Read time", value: `${result.metrics.estimatedReadingMinutes}min` },
                        ].map((m) => (
                          <div key={m.label} className="rounded-lg border border-[#EAEAE7] bg-white px-2.5 py-2">
                            <p className="text-[10px] text-[#9CA1A8]">{m.label}</p>
                            <p className="text-sm font-bold text-[#1B1D21]">{m.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Schema signals */}
                {result.metrics && (
                  <div className="rounded-[10px] border border-[#EAEAE7] bg-[#F9F9F7] p-4">
                    <p className="mb-3 text-[13px] font-semibold text-[#1B1D21]">Structured data and AI signal coverage</p>
                    <div className="grid gap-2 md:grid-cols-4">
                      {[
                        { label: "JSON-LD (structured data for search engines)", pass: result.metrics.hasJsonLd },
                        { label: "FAQ structured data (helps search engines show Q&A directly in results)", pass: result.metrics.hasFaqSchema },
                        { label: "Article structured data (helps search engines understand article metadata)", pass: result.metrics.hasArticleSchema },
                        { label: "Breadcrumb structured data", pass: result.metrics.hasBreadcrumbSchema },
                        { label: "Canonical tag (tells Google which page version is the original)", pass: result.metrics.hasCanonical },
                        { label: "robots.txt (file that tells search engines which pages to crawl)", pass: result.metrics.hasRobotsTxt },
                        { label: "Sitemap (XML file listing all pages for search engines to discover)", pass: result.metrics.hasSitemap },
                        { label: "llms.txt (file that helps AI models understand your site content)", pass: result.metrics.hasLlmsTxt },
                        { label: "Author attribution (identifies the content author and builds trust with search engines)", pass: result.metrics.hasAuthorAttribution },
                      ].map((sig) => (
                        <div
                          key={sig.label}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                            sig.pass ? "border-[#CDE8D9] bg-[#E8F5EE]" : "border-[#FECACA] bg-[#FFF5F5]"
                          }`}
                        >
                          <span className={`text-sm font-bold ${sig.pass ? "text-[#1F7A48]" : "text-[#DC2626]"}`}>
                            {sig.pass ? "✓" : "✕"}
                          </span>
                          <p className={`text-[12px] font-semibold ${sig.pass ? "text-[#1F7A48]" : "text-[#B91C1C]"}`}>
                            {sig.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sitewide sample */}
                {result.sitewideSample && result.sitewideSample.sampledPages.length > 0 && (
                  <div className="rounded-[10px] border border-[#EAEAE7] bg-[#F9F9F7] p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-[13px] font-semibold text-[#1B1D21]">Sitewide sample</p>
                      <p className="text-xs text-[#9CA1A8]">
                        {result.sitewideSample.sampleSize} pages · avg {result.sitewideSample.avgResponseTimeMs}ms
                      </p>
                    </div>
                    <div className="mb-3 grid grid-cols-4 gap-2">
                      {[
                        { label: "Title", value: `${result.sitewideSample.titleCoverage}%` },
                        { label: "Meta desc", value: `${result.sitewideSample.metaDescriptionCoverage}%` },
                        { label: "Canonical tags", value: `${result.sitewideSample.canonicalCoverage}%` },
                        { label: "Structured data", value: `${result.sitewideSample.jsonLdCoverage}%` },
                      ].map((s) => (
                        <div key={s.label} className="rounded-lg border border-[#EAEAE7] bg-white px-2.5 py-2 text-center">
                          <p className="text-[10px] text-[#9CA1A8]">{s.label}</p>
                          <p className="text-sm font-bold text-[#1B1D21]">{s.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-[#EAEAE7] bg-white">
                      <table className="min-w-full text-left text-xs">
                        <thead className="bg-[#F9F9F7] text-[#6A6E75]">
                          <tr>
                            {["URL", "Status", "Speed", "Title", "Meta", "Canonical", "JSON-LD"].map((h) => (
                              <th key={h} className="px-2.5 py-2 font-semibold">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.sitewideSample.sampledPages.map((page) => (
                            <tr key={page.url} className="border-t border-[#EAEAE7] text-[#1B1D21]">
                              <td className="max-w-[200px] truncate px-2.5 py-2" title={page.url}>{page.url}</td>
                              <td className={`px-2.5 py-2 font-semibold ${page.status === 200 ? "text-[#1F7A48]" : "text-[#DC2626]"}`}>{page.status}</td>
                              <td className="px-2.5 py-2">{page.responseTimeMs}ms</td>
                              <td className="px-2.5 py-2">{page.titlePresent ? "✓" : "✕"}</td>
                              <td className="px-2.5 py-2">{page.metaDescriptionPresent ? "✓" : "✕"}</td>
                              <td className="px-2.5 py-2">{page.canonicalPresent ? "✓" : "✕"}</td>
                              <td className="px-2.5 py-2">{page.jsonLdPresent ? "✓" : "✕"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── CHECKS TAB ───────────────────────────────── */}
            {activeTab === "checks" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[13px] text-[#6A6E75]">
                    <span className="font-bold text-[#1F7A48]">{passFail.passed}</span> passed ·{" "}
                    <span className="font-bold text-[#DC2626]">{passFail.failed}</span> failed
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedCategories(
                        expandedCategories.size === groupedChecks.length
                          ? new Set()
                          : new Set(groupedChecks.map((g) => g.category))
                      )
                    }
                    className="text-[11px] font-semibold text-[#3D5BDB] hover:underline"
                  >
                    {expandedCategories.size === groupedChecks.length ? "Collapse all" : "Expand all"}
                  </button>
                </div>

                {groupedChecks.map((group) => {
                  const passed = group.checks.filter((c) => c.passed).length;
                  const pct = Math.round((passed / group.checks.length) * 100);
                  const isOpen = expandedCategories.has(group.category);
                  return (
                    <div key={group.category} className="rounded-[10px] border border-[#EAEAE7]">
                      <button
                        type="button"
                        onClick={() => toggleCategory(group.category)}
                        className="flex w-full items-center justify-between rounded-[10px] bg-[#F9F9F7] px-4 py-3 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-2 w-2 rounded-full" style={{ background: pct >= 85 ? "#1F7A48" : pct >= 60 ? "#D97706" : "#DC2626" }} />
                          <p className="text-[13px] font-semibold text-[#1B1D21]">{group.category}</p>
                          <span className="text-xs text-[#9CA1A8]">{passed}/{group.checks.length}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-xs font-bold" style={{ color: pct >= 85 ? "#1F7A48" : pct >= 60 ? "#D97706" : "#DC2626" }}>{pct}%</p>
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9CA1A8" strokeWidth="1.5" className={`transition-transform ${isOpen ? "rotate-180" : ""}`}>
                            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      </button>

                      {isOpen && (
                        <div className="grid gap-2 p-3 md:grid-cols-2">
                          {group.checks.map((check) => (
                            <div key={check.key} className="rounded-lg border border-[#EAEAE7] bg-white px-3 py-2.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <p className="truncate text-[12px] font-semibold text-[#1B1D21]">{check.label}</p>
                                    {check.weight !== undefined && (
                                      <span className="shrink-0 rounded bg-[#F9F9F7] px-1 py-0.5 text-[10px] text-[#9CA1A8]">
                                        w:{check.weight}
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 text-xs text-[#6A6E75]">{check.details}</p>
                                </div>
                                <PassBadge passed={check.passed} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── GEO SIGNALS TAB ──────────────────────────── */}
            {activeTab === "geo-signals" && result.geoSemantic && (
              <div className="space-y-5">
                <div className="flex items-end justify-between rounded-[10px] border border-[#D8E0F8] bg-[#EEF2FC] p-4">
                  <div>
                    <p className="text-[13px] font-semibold text-[#1B1D21]">GEO semantic analysis</p>
                    <p className="text-xs text-[#6A6E75]">Entity clarity · citation readiness · source credibility · freshness · answer extraction</p>
                  </div>
                  <p className="text-3xl font-bold text-[#3D5BDB]">{result.geoSemantic.overallScore}%</p>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {result.geoSemantic.checks.map((check) => (
                    <div key={check.key} className="rounded-lg border border-[#EAEAE7] bg-white p-3">
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <p className="text-[12px] font-semibold text-[#1B1D21]">{check.label}</p>
                        <p className={`text-sm font-bold ${
                          check.score >= 80 ? "text-[#1F7A48]" : check.score >= 60 ? "text-[#D97706]" : "text-[#DC2626]"
                        }`}>{check.score}%</p>
                      </div>
                      <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-[#EAEAE7]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${check.score}%`,
                            background: check.score >= 80 ? "#1F7A48" : check.score >= 60 ? "#D97706" : "#DC2626",
                          }}
                        />
                      </div>
                      <p className="text-[11px] text-[#6A6E75]">{check.details}</p>
                    </div>
                  ))}
                </div>

                {result.geoSemantic.priorities.length > 0 && (
                  <div className="rounded-[10px] border border-[#D8E0F8] bg-white p-4">
                    <p className="mb-2 text-[13px] font-semibold text-[#1B1D21]">Top GEO signal priorities</p>
                    <ul className="list-disc space-y-1.5 pl-5 text-xs text-[#6A6E75]">
                      {result.geoSemantic.priorities.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* ── ACTION PLAN TAB ───────────────────────────── */}
            {activeTab === "action-plan" && (
              <div className="space-y-5">
                {/* Conversion CTA (styles in geo-audit.css, ga-cta-*) */}
                <div className="ga-cta-card">
                  <div className="ga-cta-copy">
                    <p className="ga-cta-title">Want these fixed on autopilot?</p>
                    <p className="ga-cta-body">
                      Ranksector publishes a new optimized article every day and handles the structured data, metadata, and internal linking this audit checks for.
                    </p>
                  </div>
                  <Link href="/signup" className="ga-cta-btn">
                    Start free trial
                  </Link>
                </div>

                {result.recommendations.length > 0 && (
                  <div className="rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB] p-4">
                    <p className="mb-2 text-[13px] font-semibold text-[#92400E]">Priority fixes</p>
                    <ul className="list-disc space-y-1.5 pl-5 text-xs text-[#78350F]">
                      {result.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {result.aiInsights && (
                  <div className="rounded-[10px] border border-[#D8E0F8] bg-[#EEF2FC] p-4">
                    <p className="mb-1 text-[13px] font-semibold text-[#1B1D21]">AI GEO insights</p>
                    <p className="text-xs text-[#6A6E75]">{result.aiInsights.summary}</p>
                    <ul className="mt-2 list-disc space-y-1.5 pl-5 text-xs text-[#6A6E75]">
                      {result.aiInsights.priorities.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                    <p className="mt-2 text-[11px] text-[#9CA1A8]">Model: {result.aiInsights.model}</p>
                  </div>
                )}

                <div className="rounded-[10px] border border-[#EAEAE7] bg-[#F9F9F7] p-4">
                  <p className="mb-1 text-[13px] font-semibold text-[#1B1D21]">Export report</p>
                  <p className="mb-3 text-xs text-[#6A6E75]">Download the full audit data for your records or to share with your team.</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={exportJson}
                      className="rounded-[10px] border border-[#EAEAE7] bg-white px-4 py-2 text-xs font-semibold text-[#1B1D21] transition-colors hover:border-[#3D5BDB] hover:text-[#3D5BDB]"
                    >
                      Export JSON
                    </button>
                    <button
                      type="button"
                      onClick={exportCsv}
                      className="rounded-[10px] border border-[#EAEAE7] bg-white px-4 py-2 text-xs font-semibold text-[#1B1D21] transition-colors hover:border-[#3D5BDB] hover:text-[#3D5BDB]"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>

                <div className="rounded-[10px] border border-[#EAEAE7] bg-white p-4">
                  <p className="mb-1 text-[13px] font-semibold text-[#1B1D21]">Audit method</p>
                  <p className="text-xs text-[#6A6E75]">
                    Live URL analysis across 35 weighted checks in 5 categories (Technical Foundation, Indexability,
                    Metadata, Content Quality, AI Retrieval Signals). Includes sitewide page sampling, internal-link
                    health checks, image-alt coverage, structured data detection (helps Google understand your content), and semantic GEO analysis.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
